import { Directive, ModelSignal, Signal, WritableSignal, inject, signal } from "@angular/core";
import { MatSort, MatSortable, SortDirection } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Observable, of } from "rxjs";
import { Tab, TabLoaderDiameter, TabLoaderStrokeWidth, ViewState } from "@app/shared/models/tab.model";
import { FormDataModel, Field, FieldRule, FieldRules } from "@app/shared/models/form-field.model";
import { FormService } from "@app/core/services/form.service";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { TabService } from "@app/core/services/tab.service";
import { NotifyService } from "@app/core/services/notify.service";
import { DateService } from "@app/core/services/date.service";
import { I18nService } from "@app/core/services/i18n.service";
import { FormGroupErrorCodes } from "@app/shared/constants/form-error-codes.constants";
import { ICONS, ICON_FONT_SET } from "@app/shared/constants/icons.constants";
import { ModalSheetService } from "@app/core/services/modal-sheet.service";
import { SystemSettingsService } from "@app/core/services/system-settings.service";
import { ThemeService } from "@app/core/services/theme.service";
import { TABLE_SIZE_OPTIONS } from "@app/shared/models/system-settings.model";
import { BadgeService } from "@app/core/services/badge.service";
import { ButtonClickEvents } from "@app/shared/models/context-menu.model";
import { BreakpointService } from "@app/core/services/breakpoint.service";
import { MenuOption, MenuOptionId } from "@app/shared/models/menu.model";
import { MenuOptionService } from "@app/core/services/menu-option.service";
import { MASK } from "@app/shared/constants/mask-constants";
import { TableInputFilterComponent } from "@app/shared/components/table-input-filter/table-input-filter.component";
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { PortalSettingsService } from "@app/core/services/portal-settings.service";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { environment } from "@env/environment";
import { UserEventsService } from "@app/core/services/user-events.service";
import { ModalResponse, ModalParms } from "@app/shared/models/modal.model";
import { JsonSettingsService } from "@app/shared/components/json-settings/json-settings.service";
import { UtilityService } from "@app/core/services/utility.service";

/**
 * Interface: BaseCrudComponentInterface
 */
interface BaseComponentInterface {
  doCreateData: (x?: any) => void;
  doUpdateData: (x?: any) => void;
  doDeleteData: (x?: any) => void;
}

/**
 * enum: CrudModes
 */
export enum CrudModes {
  Create,
  Read,
  Update,
  Delete
}

/**
 * @class: BaseComponent
 *
 * Base class for all components.
 *
 * Purpose is to encapsulate common services and implements common methods
 *
 */
@Directive()
export abstract class BaseComponent {
  formService = inject(FormService);
  authService = inject(AuthenticationService);
  userSettingsService = inject(UserSettingsService);
  tabService = inject(TabService);
  notifyService = inject(NotifyService);
  dateService = inject(DateService);
  systemSettings = inject(SystemSettingsService);
  portalSettingsService = inject(PortalSettingsService);
  i18nService = inject(I18nService);
  formBuilder = inject(FormBuilder);
  themeService = inject(ThemeService);
  menuOptionService = inject(MenuOptionService);
  modalSheetService = inject(ModalSheetService);
  userEventService = inject(UserEventsService);
  badgeService = inject(BadgeService);
  breakpointService = inject(BreakpointService);
  jsonSettingsService = inject(JsonSettingsService);
  utilityService = inject(UtilityService);

  /**
   * Variables
   */
  private _showForm: WritableSignal<boolean> = signal(false);
  private _hasRecords: WritableSignal<boolean> = signal(false);
  private _showData: WritableSignal<boolean> = signal(false);
  private _pagingState: string = "previous";

  /**
   * tab object
   *
   * This is a reference to the tab the current component is a child of
   *
   * Implement by adding the following code in your component class:
   *       tab = model<Tab>();
   *
   * If the tab is unused in the component, implement it by the following:
   *     tab: undefined;
   */
  abstract tab: ModelSignal<Tab>;

  badgeValue = (value: number | string): string => {
    return this.badgeService.badgeValue(value);
  };

  /**
   * Check if user can access menu option
   * @param data Menu id to access
   * @param extraCheck Extra boolean value to check
   */
  canAccessMenuOption = (data: MenuOptionId[]): boolean => {
    return this.menuOptionService.canShowMenuOption(data);
  };

  /**
   * Retrieve menu option in array by id.
   * @param id
   * @returns
   */
  fetchMenuOption = (data: MenuOptionId): MenuOption => {
    return this.menuOptionService.getMenuOption(data.id);
  };

  /**
   * Wait until an object is fully created.
   * Use this method if needing to wait until a view child is fully rendered.
   * Use this method instead of a settimeout().
   * @param parent
   * @param viewChildName
   * @param signal : Set to true when waiting for a viewChild signal to be completed.
   * @param refreshRate
   * @param maxWaitTime
   * @returns
   */
  waitUntilObjectIsReady(
    parent: any,
    viewChildName: string = null,
    signal: boolean = true,
    refreshRate: number = 15,
    maxWaitTime: number = 3000
  ): Observable<boolean> {
    return this.utilityService.waitUntilObjectIsReady(parent, viewChildName, signal, refreshRate, maxWaitTime);
  }

  isObjectDefined = <T>(value: T | undefined | null): boolean => {
    return this.utilityService.isObjectDefined(value);
  };

  /**
   * Fetches debounce value
   * @returns
   */
  getDebounce(): number {
    return this.portalSettingsService.getDebounce();
  }

  /**
   * Wait and set paginator to TableDataSource
   * @param tableSource
   * @param paginator
   * @returns
   */
  setPaginator(tableSource: MatTableDataSource<any>, paginator: Signal<MatPaginator>) {
    if (!paginator()) {
      return;
    }

    this.waitUntilObjectIsReady(paginator).subscribe((r) => {
      if (!r) {
        return;
      }

      tableSource.paginator = paginator();
    });
  }

  /**
   * Wait and set sort to TableDataSource
   * @param tableSource
   * @param sort
   * @returns
   */
  setSort(tableSource: MatTableDataSource<any>, sort: Signal<MatSort>) {
    if (!sort()) {
      return;
    }

    this.waitUntilObjectIsReady(sort).subscribe((r) => {
      if (!r) {
        return;
      }

      tableSource.sort = sort();
    });
  }

  /**
   * Save the current view state of the list component
   *
   * @param tableSource
   *
   * @returns ViewState
   */
  saveTableViewState(
    tableSource: MatTableDataSource<any>,
    component?: any,
    tableFilterComponent?: TableInputFilterComponent,
    data?: any,
    uniqueId?: any
  ): void {
    const vs = this.tab().data.viewStates.add(new ViewState(component, 0, 0, null, null, null, null, data, uniqueId));
    const sort = tableSource?.sort;

    if (sort && sort.active) {
      const direction: SortDirection = sort["_direction"];
      if (direction) {
        const sortable: MatSortable = {
          id: sort.active,
          start: direction,
          disableClear: false
        };
        vs.sortable = sortable;
      }
    }

    if (tableSource?.paginator) {
      vs.pageIndex = tableSource.paginator.pageIndex;
      vs.pageSize = tableSource.paginator.pageSize;
    }

    if (tableFilterComponent) {
      vs.filter = tableFilterComponent.filter;
      vs.searchItems = tableFilterComponent.searchItems;
    }
  }

  /**
   * Restore the table view state
   *
   * @param tableSource
   */
  restoreTableViewState(tableSource: MatTableDataSource<any>, component: any, tableFilterComponent: TableInputFilterComponent, uniqueId?: any): void {
    const vs = this.tab()?.data?.viewStates?.viewStates?.find((x) => x.component === component && x?.uniqueId === uniqueId);
    if (!vs) {
      return;
    }

    // Restore Filter and search items
    if (tableFilterComponent) {
      tableFilterComponent.resetFilter(vs.searchItems, vs.filter, false, JSON.parse(JSON.stringify(vs.searchItems)));
    }

    if (vs?.sortable) {
      // Restore Sort
      const sort = tableSource.sort;
      sort.sort({
        id: vs.sortable.id,
        disableClear: vs.sortable.disableClear,
        start: vs.sortable.start
      });
    }

    // Restore Paginator
    const paginator = tableSource?.paginator as MatPaginator;
    if (paginator) {
      paginator.pageSize = vs.pageSize;

      if (paginator.getNumberOfPages() < vs.pageIndex) {
        paginator.lastPage();
      } else {
        paginator.pageIndex = vs.pageIndex;
      }

      paginator._changePageSize(vs.pageSize);
    }

    this.tab().data.viewStates.delete();
  }

  fetchViewState(component: any, uniqueId?: any): ViewState {
    let vs = this.tab().data.viewStates.currentViewState;

    // If no match found, try and find it
    if (vs?.component !== component) {
      vs = this.tab().data.viewStates.viewStates.find((x) => x.component === component);
    }

    if (vs?.component === component && vs?.uniqueId === uniqueId) {
      return vs;
    }

    return undefined;
  }

  onPageStateChange(event: PageEvent) {
    this._pagingState = event.previousPageIndex > event.pageIndex ? "previous" : "next";
  }

  /**
   * Load user settings and set portal options
   * @returns
   */
  loadUserSettings() {
    return this.userSettingsService.loadSettings();
  }

  /**
   * Update filter content for showing no results found screen.
   * @param dataContext
   */
  onFilterChanged(dataLength: number): void {
    this.hasRecords.set(dataLength > 0);
  }

  userDefinedIconFontSet = (ignoreSolid?: boolean, checkAppearance?: boolean) => {
    return this.themeService.userDefinedIconAppearance(ignoreSolid, checkAppearance);
  };

  /**
   * @param path
   * @returns
   */
  getBooleanValue = (path: string): boolean => {
    return this.jsonSettingsService.getBooleanValue(path);
  };

  /**
   * Close the current tab
   */
  closeTab(): void {
    this.tabService.removeTab(this.tab());
  }

  get pagingState(): string {
    return this._pagingState;
  }

  set pagingState(v: string) {
    this._pagingState = v;
  }

  /**
   * Set whether the form or section associated with component should be displayed
   *
   * Implemented in component HTML as:
   *
   * @if (showForm()) {}
   *
   */
  get showForm(): WritableSignal<boolean> {
    return this._showForm;
  }
  set showForm(v: boolean) {
    this._showForm.set(v);
  }

  /**
   * Set whether the table has records. This will either display a table or some type of message indicating
   * that no records exist.
   *
   * Implemented in component HTML as:
   *
   *    ...
   *    </table>
   *    @if(!hasRecords()) {
   *      <app-no-records-found></app-no-records-found>
   *    }
   *    <mat-paginator>
   *
   *
   * Implemented in component typescript as:
   *
   *    setTableDataSource(data) {
   *      ...
   *      this.hasRecords.set(data?.length > 0);
   *      ...
   *    }
   */
  get hasRecords(): WritableSignal<boolean> {
    return this._hasRecords;
  }

  set hasRecords(v: boolean) {
    this._hasRecords.set(v);
  }

  /**
   * Set whether the data is shown.
   * The value will be handled by the show-data directive.
   */
  get showData(): WritableSignal<boolean> {
    return this._showData;
  }

  set showData(v: boolean) {
    this._showData.set(v);
  }

  /**
   * @returns the current language
   */
  get currentLanguage(): string {
    return this.i18nService.language;
  }

  /**
   * @returns number of items to display on a page
   */
  get pageSize(): number {
    return this.userSettingsService.tableSettings.tablePageSize;
  }

  /**
   * @returns number of items to display on a page with a hidden paginator. If > then amount show paginator.
   */
  get hiddenPaginatorPageSize(): number {
    return 50;
  }

  /**
   * @returns page size options to display to the user
   */
  get pageSizeOptions(): number[] {
    return TABLE_SIZE_OPTIONS;
  }

  /**
   * @returns page size options to display to the user
   */
  get showFirstLastButtons(): boolean {
    return this.userSettingsService.tableSettings.tableShowFirstLastButtons;
  }

  /**
   * Gets an sets the value for an individual icon
   * @param icon : the constant value you want to get form the icons.constant.ts file
   * @see `ICONS.CLOSE`
   */
  /**
   * @returns a constants list of all icons in the application
   */
  get icons() {
    return ICONS;
  }

  /**
   * @returns application font set
   */
  get iconFontSet() {
    return ICON_FONT_SET;
  }

  /**
   * @returns masks.
   */
  get masks() {
    return MASK;
  }

  get buttonClickEvents(): typeof ButtonClickEvents {
    return ButtonClickEvents;
  }

  /**
   * @returns if the browsers breakpoint is mobile.
   */
  get isMobile(): WritableSignal<boolean> {
    return this.breakpointService.isMobile;
  }

  /**
   * @returns if the browsers breakpoint is medium.
   */
  get isMedium(): WritableSignal<boolean> {
    return this.breakpointService.isMedium;
  }

  /**
   * @returns if the browsers breakpoint is large.
   */
  get isLarge(): WritableSignal<boolean> {
    return this.breakpointService.isLarge;
  }

  /**
   * Retrieve url for portal based on dev or production environment
   */
  get baseUrl(): string {
    if (environment.production) {
      return window.location.origin;
    }

    return environment.serverUrl;
  }

  /**
   * Retrieve tabLoader standardized diameter value for loader-spinner
   */
  get tabLoaderDiameter(): number {
    return TabLoaderDiameter;
  }

  /**
   * Retrieve tabLoader standardized strokeWidth value for loader-spinner
   */
  get tabLoaderStrokeWidth(): number {
    return TabLoaderStrokeWidth;
  }
}

/**
 * @class BaseFormGroupComponent
 *
 * Class component to implement components usinf form groups
 */
@Directive()
export abstract class BaseFormGroupComponent extends BaseComponent {
  /**
   * Variables
   */
  private _formGroup: FormGroup;
  private _dataModel: FormDataModel;
  private hasServerErrors: boolean = false;

  /**
   * dataContext
   *
   * Contains a data model of the information being sent and received via web services and
   * loaded to and from form group controls.
   */
  abstract dataContext: any;

  /**
   * Constructor
   */

  /**
   * Check if form is dirty before closing component or tab
   */
  checkDirtyForm(data: ModalParms): Observable<ModalResponse> {
    if (this._formGroup?.dirty) {
      if (this.authService.checkDontShowAgainId(data?.showDontAskAgainId)) {
        return of({ save: true } as ModalResponse);
      } else {
        return this.modalSheetService.openDirtyModal(data);
      }
    } else {
      return of({ discard: true } as ModalResponse); // clean form, we can leave
    }
  }

  /**
   * Check if form is dirty before closing component or tab
   */
  checkDirtyFormCaution(data?: ModalParms): Observable<ModalResponse> {
    if (this._formGroup?.dirty) {
      if (this.authService.checkDontShowAgainId(data?.showDontAskAgainId)) {
        return of({ save: true } as ModalResponse);
      } else {
        return this.modalSheetService.openCautionModal(data);
      }
    } else {
      return of({ discard: true } as ModalResponse); // clean form, we can leave
    }
  }

  /**
   * setFormControl()
   *
   * Set data model values to form control values
   *
   * @param formGroup
   * @param dataContext
   */
  setFormControl(formGroup: FormGroup, dataContext: any): void {
    this.hasServerErrors = false;
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormControl) {
        if (key in dataContext) {
          if (!(dataContext[key] as Field).error) {
            formGroup.get(key).setValue(dataContext[key].value);
          }
          if ((dataContext[key] as Field).error !== "") {
            this.hasServerErrors = true;
            formGroup.get(key).setErrors({ serverError: true });
            formGroup.get(key).markAsTouched();
          } else {
            formGroup.get(key).setErrors(null);
          }
        }
      } else if (control instanceof FormGroup) {
        this.setFormControl(control, dataContext);
      }
    });
  }

  /**
   * Set form control values to data model values
   *
   * @param formGroup
   * @param dataContext
   */
  setDataModel(formGroup: FormGroup, dataContext: any): void {
    // Signal variant
    if (typeof dataContext === "function") {
      this.setDataModelBySignal(formGroup, dataContext);
      return;
    }

    if (typeof dataContext !== "object") {
      throw "dataContext is not an object, pass in proper variable";
    }

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormControl) {
        if (key in dataContext) {
          dataContext[key].value = formGroup.get(key).value;
        }
      } else if (control instanceof FormGroup) {
        this.setDataModel(control, dataContext);
      }
    });
  }

  /**
   * Set form control values to data model values when dataContext is a signal
   *
   * @param formGroup
   * @param dataContext
   */
  setDataModelBySignal(formGroup: FormGroup, dataContext: WritableSignal<any>): void {
    dataContext.update((data) => {
      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        if (control instanceof FormControl) {
          if (key in data) {
            data[key].value = control.value;
          }
        }
      });
      return { ...data };
    });
  }

  /**
   * Get data model and field rules
   *
   * @return Observable<FormDataModel>
   */
  getDataModel(url: any): Observable<FormDataModel> {
    return this.formService.fetchDataModel(url);
  }

  /**
   * Validate all form fields
   *
   * Recalculates the value and validation status of the control.
   *
   * @param formGroup
   */
  validateAllFormFields(formGroup: FormGroup): void {
    if (!this.hasServerErrors) {
      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        if (control instanceof FormControl) {
          control.updateValueAndValidity({ onlySelf: true });
          if (control.invalid) {
            if (this instanceof BaseCrudComponent) {
              if ((this as BaseCrudComponent).crudMode !== CrudModes.Create) {
                control.markAsTouched();
              }
            }
          }
        } else if (control instanceof FormGroup) {
          this.validateAllFormFields(control);
        }
      });
    }
    formGroup.updateValueAndValidity();
  }

  /**
   * Get the control validators and determine if it's required.
   */
  isRequired(controlName: string, control?: AbstractControl): boolean {
    try {
      const validator: ValidationErrors = (control ?? this.formGroup.get(controlName))?.validator({} as AbstractControl);
      return validator?.required ?? false;
    } catch {
      return false;
    }
  }

  /**
   * validatorBuilder()
   *
   * Build an array of validators for a control based on field rules returned from the server
   *
   * @param fieldRule
   * @param formValidators : custom validators like email address regex.
   */
  validatorBuilder(fieldRule: FieldRule | FieldRules, formValidators?: object): ValidatorFn[] {
    return this.formService.validatorBuilder(fieldRule, formValidators);
  }

  /**
   * Get a form group control error message
   *
   * @param formGroup
   * @param control
   * @param customErrorCode : error code for the control; could be an existing FormGroupErrorCodes or custom
   * @param customErrorMsg : error message to be displayed for the customErrorCode
   */
  getError(formGroup: FormGroup, control: string | string[], customErrorCode?: string, customErrorMsg?: string, formControl?: FormControl): string {
    let error: string = "";
    let controls: string[];

    if (formGroup && !formGroup.invalid) {
      return error;
    }

    if (Array.isArray(control)) {
      controls = control;
    } else {
      controls = [control];
    }

    for (const e in FormGroupErrorCodes) {
      if (this._hasError(formGroup, controls, FormGroupErrorCodes[e], formControl)) {
        switch (e) {
          case FormGroupErrorCodes.required:
            error = "ERR_VALUE_REQUIRED";
            break;
          case FormGroupErrorCodes.maxlength:
            error = "ERR_VALUE_MAXLENGTH";
            break;
          case FormGroupErrorCodes.minlength:
            error = "ERR_VALUE_MINLENGTH";
            break;
          case FormGroupErrorCodes.max:
            error = "ERR_VALUE_MAX";
            break;
          case FormGroupErrorCodes.min:
            error = "ERR_VALUE_MIN";
            break;
          case FormGroupErrorCodes.pattern:
            error = "ERR_VALUE_PATTERN";
            break;
          case FormGroupErrorCodes.maskError:
            error = "ERR_VALUE_MASK";
            break;
          case FormGroupErrorCodes.invalidRange:
            error = "ERR_VALUE_RANGE";
            break;
          case FormGroupErrorCodes.matStartDateInvalid:
            error = "ERR_VALUE_RANGE";
            break;
          case FormGroupErrorCodes.matEndDateInvalid:
            error = "ERR_VALUE_RANGE";
            break;
          case FormGroupErrorCodes.matDatepickerMin:
            error = "ERR_VALUE_MIN";
            break;
          case FormGroupErrorCodes.matDatepickerMax:
            error = "ERR_VALUE_MAX";
            break;
          case FormGroupErrorCodes.confirmMatch:
            error = "ERR_NO_MATCH";
            break;
          case FormGroupErrorCodes.serverError:
            controls.some((e) => {
              error = this.getServerError(e);
              return error !== "";
            });
            break;
          default:
            break;
        }
      }

      if (error !== "") {
        break;
      }
    }

    // Custom error checking
    if (customErrorCode && customErrorMsg) {
      if (this._hasError(formGroup, controls, customErrorCode)) {
        error = customErrorMsg;
      }
    }

    return error;
  }

  /**
   * Get field level errors from server
   *
   * @param control
   * @returns string
   */
  getServerError(control: string): string {
    let error: string = "";
    try {
      error = (this.dataContext[control] as Field).error;
    } catch {}
    return error;
  }

  /**
   * Check if a control has a particular error set
   *
   * @param formGroup
   * @param control
   * @param errorCode
   *
   * @returns boolean
   */
  private _hasError(formGroup: FormGroup, controls: string[], errorCode: string, control?: FormControl): boolean {
    if (control) {
      if (!(control.dirty || control.touched)) {
        return false;
      }
      return control.hasError(errorCode);
    }

    return controls.some((e) => {
      if (e in formGroup.controls) {
        if (!(formGroup.controls[e].dirty || formGroup.controls[e].touched)) {
          return false;
        }
        return formGroup.controls[e].hasError(errorCode);
      }

      return false;
    });
  }

  /**
   * Component's Form Group object
   */
  get formGroup(): FormGroup {
    return this._formGroup;
  }
  set formGroup(v: FormGroup) {
    this._formGroup = v;
  }

  /**
   * Data model contains the following data structure
   *
   * FormDataModal:
   *  - model: data model used to load into a data context
   *  - fieldRules: field rules to be used to build validators for controls,
   */
  get dataModel(): FormDataModel {
    return this._dataModel;
  }
  set dataModel(v: FormDataModel) {
    this._dataModel = v;
  }
}

/**
 * @class BaseCrudComponent
 *
 * Class component to implement CRUD processes
 */
@Directive()
export abstract class BaseCrudComponent extends BaseFormGroupComponent implements BaseComponentInterface {
  /**
   * Constructor
   */

  /**
   * Variables
   */
  private _crudMode: CrudModes = CrudModes.Read;

  abstract doCreateData(x?: any): void;
  abstract doUpdateData(x?: any): void;
  abstract doDeleteData(x?: any): void;

  /**
   * Set or get the current form's data mode.
   *
   * Possible modes:
   *  - Create: Data will be used to create a new record
   *  - Read: Data is in read only mode
   *  - Update: Data is in update mode
   *  - Delete: Data will be deleted
   */
  get crudMode(): CrudModes {
    return this._crudMode;
  }
  set crudMode(v: CrudModes) {
    if (v in CrudModes) {
      this._crudMode = v;
    } else {
      throw "Crud mode invalid! Please use a valid crud mode.";
    }
  }

  /**
   * Return true if we are in create mode
   */
  get isCreateMode(): boolean {
    return this._crudMode === CrudModes.Create;
  }

  /**
   * Return true if we are in update mode
   */
  get isUpdateMode(): boolean {
    return this._crudMode === CrudModes.Update;
  }

  /**
   * Return true if we are in read only mode
   */
  get isReadOnlyMode(): boolean {
    return this._crudMode === CrudModes.Read;
  }

  /**
   * Return true if we are in delete mode
   */
  get isDeleteMode(): boolean {
    return this._crudMode === CrudModes.Delete;
  }
}
